import GradientStop from './gradient-stop';
import LinearGradient from './linear-gradient';
import RadialGradient from './radial-gradient';

/**
 * Utility methods for gradients
 */
export default class GradientHelpers {
  /**
   * SVG Namespace
   */
  static NS = 'http://www.w3.org/2000/svg';

  static getSceneDefs () {
    let defs = document.querySelector('#svgcontent defs');
    if (!defs) {
      defs = document.createElementNS(GradientHelpers.NS, 'defs');
      document.querySelector('#svgcontent').prepend(defs);
    }
    return defs;
  }

  /**
   * Get scene gradients
   * @returns An array of scene gradients descriptions
   */
  static getGradientsList () {
    const result = {
      linearGradients: [],
      radialGradients: []
    };

    const defs = GradientHelpers.getSceneDefs();

    const linearGradients = defs.querySelectorAll('linearGradient');
    linearGradients.forEach(linearGradient => result.linearGradients.push({ id: linearGradient.id }));
    const radialGradients = defs.querySelectorAll('radialGradient');
    radialGradients.forEach(radialGradient => result.radialGradients.push({ id: radialGradient.id }));

    return result;
  }

  /**
   *
   * @param {string} gradientId
   * @returns {SVGGradientElement}
   */
  static getGradientById (gradientId) {
    return GradientHelpers.getSceneDefs().querySelector(`#${gradientId}`);
  }

  static getNewGradientId () {
    return `gradient-${(Math.random() * 1e6) | 0}`;
  }

  /**
   * Add a linear gradient to the scene
   * @param {LinearGradient|RadialGradient} gradient
   */
  static addGradient (gradient) {
    const gradientElement = document.createElementNS(GradientHelpers.NS, gradient instanceof LinearGradient ? 'linearGradient' : 'radialGradient');

    // Set attributes
    gradientElement.id = GradientHelpers.getNewGradientId();
    gradient.toElement(gradientElement);

    // Add stops
    for (let i = 0, length = gradient.stops.length; i < length; i++) {
      const stopElement = document.createElementNS(GradientHelpers.NS, 'stop');
      const stop = gradient.stops[i];
      stop.toElement(stopElement);
      gradientElement.appendChild(stopElement);
    }

    // Add to defs
    GradientHelpers.getSceneDefs().appendChild(gradientElement);
  }

  /**
   * Make a copy of SVG gradient element
   * @param {string} gradientId
   */
  static duplicateGradientElement (gradientId) {
    const gradientElement = GradientHelpers.getGradientById(gradientId);
    if (!gradientElement) return null;
    const newGradientElement = gradientElement.cloneNode(true);
    newGradientElement.id = GradientHelpers.getNewGradientId();
    gradientElement.parentElement.appendChild(newGradientElement);
    return newGradientElement;
  }

  /**
   * Converts SVG gradient element between linear/radial types
   * @param {string} gradientId
   * @returns
   */
  static convertGradientElement (gradientId) {
    const gradientElement = GradientHelpers.getGradientById(gradientId);
    if (!gradientElement) return null;

    const isLinear = gradientElement.tagName === 'linearGradient';

    let baseGradient, targetGradient;
    if (isLinear) {
      baseGradient = new LinearGradient(gradientElement);
      targetGradient = new RadialGradient();
    } else {
      baseGradient = new RadialGradient(gradientElement);
      targetGradient = new LinearGradient();
    }

    targetGradient.stops = [];
    baseGradient.stops.forEach(stop => {
      targetGradient.stops.push(stop.clone());
    });

    // Update SVG
    const newGradientElement = document.createElementNS(GradientHelpers.NS, isLinear ? 'radialGradient' : 'linearGradient');
    newGradientElement.id = GradientHelpers.getNewGradientId();
    targetGradient.toElement(newGradientElement);

    // Add stops
    for (let i = 0, length = targetGradient.stops.length; i < length; i++) {
      const stopElement = document.createElementNS(GradientHelpers.NS, 'stop');
      const stop = targetGradient.stops[i];
      stop.toElement(stopElement);
      newGradientElement.appendChild(stopElement);
    }

    // Add to defs
    gradientElement.parentElement.appendChild(newGradientElement);
    return newGradientElement.id;
  }

  /**
   * Delete the specified gradient element
   * @param {string} gradientId
   * @returns
   */
  static deleteGradientElement (gradientId) {
    const gradientElement = GradientHelpers.getGradientById(gradientId);
    if (gradientElement) gradientElement.remove();
  }

  /**
   * If the specified gradient element is in use
   * @param {string} gradientId
   */
  static isUsed (gradientId) {
    return document.querySelectorAll(`#svgcontent [fill="url(#${gradientId})"]`).length > 0;
  }

  /**
   *
   * @param {LinearGradient|RadialGradient} gradient
   */
  static addNewStop (gradient) {
    gradient.stops.push(new GradientStop());
    GradientHelpers.makeUniformStopOffsets(gradient);
  }

  /**
   *
   * @param {LinearGradient|RadialGradient} gradient
   * @param {number|string} index
   * @returns
   */
  static deleteStop (gradient, index) {
    if (gradient.stops.length < 3 || index < 0 || index >= gradient.stops.length) return;
    gradient.stops.splice(index, 1);
    GradientHelpers.makeUniformStopOffsets(gradient);
  }

  /**
   * Update offsets to have a uniform color distribution
   * @param {LinearGradient|RadialGradient} gradient
   */
  static makeUniformStopOffsets (gradient) {
    const offsetDelta = Math.round(100 / (gradient.stops.length - 1));
    for (let i = 0; i < gradient.stops.length; i++) {
      gradient.stops[i].offset = offsetDelta * i;
    }
  }

  /**
   * Apply the specified gradient to the selected objects
   * @param {string} gradientId
   */
  static applyGradientToSelection (gradientId) {
    const selection = INKIVE.EditorFacade.getSelection();
    selection.forEach(selected => {
      switch (selected.type) {
        case 'shape':
          INKIVE.DesignFacade.set(selected.id, { fill: { color: `url(#${gradientId})` } });
          break;
        case 'text':
        case 'background':
          INKIVE.DesignFacade.set(selected.id, { color: `url(#${gradientId})` });
          break;
      }
    });
  }
}
